import React, {useMemo, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {DatePickerInput, Dropdown, Toggle} from '@/components/atoms';
import {
  DATE_VALIDATION_TYPE,
  Constants
} from '@/utils/constants/commonConstants';
import {COUNTRY_CODES} from '@/utils/constants/globalConstants';
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants';

const durationOptions = [
  {id: 'MONTHLY', displayText: 'Monthly', value: 'MONTHLY'},
  {id: 'QUARTERLY', displayText: 'Quarterly', value: 'QUARTERLY'},
  {id: 'ANNUAL', displayText: 'Annual', value: 'ANNUAL'},
  {id: 'CUSTOM', displayText: 'Custom', value: 'CUSTOM'}
];

const AvailabilitySection = ({
  formData,
  countryCode,
  onChange,
  salesTarget,
  disableStartDate = false,
  disableEndDate = false
}) => {
  const startDate = formData?.startDate || '';
  const endDate = formData?.endDate;
  const targetType = formData?.targetType;

  // Check if editing In-Progress target
  const isInProgressTarget = useMemo(() => {
    return (
      salesTarget?.status &&
      salesTarget.status?.toLowerCase() === Constants.InProgress.toLowerCase()
    );
  }, [salesTarget?.status]);

  // Check if target was originally active when page loaded (based on server data)
  const isOriginallyActiveTarget = useMemo(() => {
    if (!salesTarget?.status) return false;
    if (salesTarget.status?.toLowerCase() !== Constants.Published.toLowerCase())
      return false;
    const originalStart =
      salesTarget?.startDate || salesTarget?.activeStartDate;
    if (!originalStart) return false;
    return new Date(originalStart) <= new Date();
  }, [
    salesTarget?.status,
    salesTarget?.startDate,
    salesTarget?.activeStartDate
  ]);

  const isEgyptAndApplicableTarget = useMemo(
    () =>
      countryCode === COUNTRY_CODES.EGYPT &&
      (targetType === TARGET_TYPES.MainTarget ||
        targetType === TARGET_TYPES.ProductTarget),
    [countryCode, targetType]
  );

  const isLinkedTargetType = useMemo(
    () =>
      targetType === TARGET_TYPES.EarlyPaymentTarget ||
      targetType === TARGET_TYPES.DropSizeTarget,
    [targetType]
  );

  const linkedTargetDates = useMemo(() => {
    const targets = formData?.linkedSalesTargets || [];
    if (!isLinkedTargetType || targets.length === 0)
      return {
        earliestStartDate: null,
        latestEndDate: null,
        minStartDate: null,
        maxEndDate: null
      };

    const parseDate = (val) => {
      if (!val) return null;
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return null;
      // Normalize to start of day to avoid timezone issues
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const fmt = (d) =>
      d
        ? `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`
        : null;

    let earliest = null;
    let latest = null;
    targets.forEach((t) => {
      const sd = parseDate(t.activeStartDate);
      const ed = parseDate(t.activeEndDate);
      if (sd && (!earliest || sd < earliest)) earliest = sd;
      if (ed && (!latest || ed > latest)) latest = ed;
    });

    // Calculate minDate for Active Start Date
    // If linked target's activeStartDate is in the past, use today's date
    // If it's in the future, use that future date
    // The date itself should be allowed (not disabled)
    let minStartDate = null;
    if (earliest) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      minStartDate = new Date(Math.max(earliest.getTime(), today.getTime()));
    }

    // Calculate maxDate for Active End Date
    // Use linked target's activeEndDate as maxDate
    // The date itself should be allowed (not disabled)
    let maxEndDate = null;
    if (latest) {
      maxEndDate = latest;
    }

    return {
      earliestStartDate: fmt(earliest),
      latestEndDate: fmt(latest),
      minStartDate: minStartDate,
      maxEndDate: maxEndDate
    };
  }, [isLinkedTargetType, formData?.linkedSalesTargets]);

  const durationValue = formData?.durationValue || formData?.duration;

  // Check if original End Date (from server) has passed - used to disable picker on initial load
  const isOriginalEndDateInPast = useMemo(() => {
    const originalEndDate = salesTarget?.endDate || salesTarget?.activeEndDate;
    if (!originalEndDate) return false;
    const end = new Date(originalEndDate);
    if (Number.isNaN(end.getTime())) return false;
    // Normalize both dates to start of day to avoid timezone/time comparison issues
    end.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today > end;
  }, [salesTarget?.endDate, salesTarget?.activeEndDate]);

  // Calculate disabled helper text for Start Date
  const startDateDisabledHelperText = useMemo(() => {
    if (isInProgressTarget) {
      return 'Active Start Date cannot be edited for In-Progress Sales Targets.';
    }
    if (isOriginallyActiveTarget) {
      return 'Active Start Date cannot be edited for Active Sales Targets.';
    }
    if (disableStartDate) {
      return 'Active Start Date cannot be edited in this state.';
    }
    return undefined;
  }, [isInProgressTarget, isOriginallyActiveTarget, disableStartDate]);

  // End date disabled/helper logic
  const {endDateDisabled, endDateHelperText} = useMemo(() => {
    let disabled = false;
    let helperText = null;

    // If editing Active target, only End Date should be editable (not disabled via this logic)
    // If editing published before start, End Date should be editable
    // If disableEndDate prop is true, force disable
    if (disableEndDate) {
      disabled = true;
      helperText = 'End Date cannot be edited in this state.';
    } else if (isOriginalEndDateInPast && salesTarget?.status) {
      // AC7: End Date update allowed only before existing End Date has passed
      // Use isOriginalEndDateInPast to check if the server's original end date has passed
      // This prevents disabling the picker when user selects an invalid date during editing
      disabled = true;
      helperText = 'End Date cannot be changed after it has passed.';
    } else if (isEgyptAndApplicableTarget) {
      if (durationValue === 'CUSTOM') {
        disabled = !startDate;
        if (!startDate) {
          helperText = 'Please select start date before selecting end date.';
        }
      } else if (
        durationValue === 'MONTHLY' ||
        durationValue === 'QUARTERLY' ||
        durationValue === 'ANNUAL'
      ) {
        disabled = true;
      }
    } else {
      disabled = !startDate;
      if (!startDate) {
        helperText = 'Please select start date before selecting end date.';
      }
    }

    return {endDateDisabled: disabled, endDateHelperText: helperText};
  }, [
    isEgyptAndApplicableTarget,
    durationValue,
    startDate,
    disableEndDate,
    isOriginalEndDateInPast,
    salesTarget?.status
  ]);

  const handleFieldChange = (field, value, validField, validValue) => {
    const updates = {...formData, [field]: value};
    if (validField !== undefined) {
      updates[validField] = validValue;
    }

    // Auto-sync Display End Date when Active End Date changes and toggle is YES
    if (field === 'endDate' && formData?.activeDisplay && isInProgressTarget) {
      updates.displayEndDate = value;
      updates.displayEndDateValid = validValue;
    }

    onChange(updates);
  };

  const handleDurationChange = (val) => {
    const selected = val?.[0];
    const updates = {
      ...formData,
      duration: selected?.displayText ?? '',
      durationValue: selected?.value ?? ''
    };

    // Auto-sync Display End Date when duration changes and toggle is YES
    if (formData?.activeDisplay && isInProgressTarget && formData?.endDate) {
      updates.displayEndDate = formData.endDate;
      updates.displayEndDateValid = formData.endDateValid;
    }

    onChange(updates);
  };

  const durationValues = useMemo(() => {
    const current = formData?.duration;
    if (!current) return [];
    const match = durationOptions.find((opt) => opt.displayText === current);
    return match ? [match] : [{id: current, displayText: current}];
  }, [formData?.duration]);

  // Track if we're currently syncing to prevent infinite loop
  const isSyncing = useRef(false);

  // Auto-sync Display End Date when Active End Date changes and toggle is YES (for In-Progress targets)
  // This handles cases where formData is updated externally (e.g., loading from server)
  useEffect(() => {
    if (isSyncing.current) {
      isSyncing.current = false;
      return;
    }

    if (isInProgressTarget && formData?.activeDisplay && formData?.endDate) {
      // Only update if Display End Date is different from Active End Date
      if (formData.displayEndDate !== formData.endDate) {
        isSyncing.current = true;
        onChange({
          ...formData,
          displayEndDate: formData.endDate,
          displayEndDateValid: formData.endDateValid
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.activeDisplay,
    formData.endDate,
    formData.displayEndDate,
    formData.endDateValid,
    isInProgressTarget,
    onChange
  ]);

  // Display dates section (when activeDisplay is false)
  const showDisplayDates = !formData?.activeDisplay;
  const displayStartDate = formData?.displayStartDate || '';

  return (
    <div className='filters-partners'>
      <div className='flex flex-row flex-wrap gap-6'>
        {/* Start Date */}
        <DatePickerInput
          value={formData?.startDate}
          updateValue={(val, validControl) =>
            handleFieldChange('startDate', val, 'startDateValid', validControl)
          }
          uiSchema={{
            className: 'box-size-width z-50',
            placeholder: 'Choose date',
            label: 'Active Start Date *',
            dependentDate: [endDate, linkedTargetDates.earliestStartDate],
            isDark: true,
            required: false,
            disabled:
              isInProgressTarget ||
              disableStartDate ||
              isOriginallyActiveTarget,
            disabledHelperText: startDateDisabledHelperText,
            minDate: linkedTargetDates.minStartDate,
            validationRules: [
              {type: 'required', msg: ` is required`},
              {
                type: DATE_VALIDATION_TYPE.START_DATE,
                msg: ` Active Start Date must be a future date.`,
                excludeLabel: true
              },
              {
                type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
                msg: ` Active Start Date must be a future date.`,
                excludeLabel: true
              },
              ...(linkedTargetDates.earliestStartDate
                ? [
                    {
                      type: DATE_VALIDATION_TYPE.END_DATE,
                      startDate: linkedTargetDates.earliestStartDate,
                      msg: ' Must be on or after the Active Start Date of the Linked Sales Target',
                      excludeLabel: true
                    }
                  ]
                : [])
            ]
          }}
        />
        {/* Duration (Egypt + Main/Product only) */}
        {isEgyptAndApplicableTarget && (
          <Dropdown
            isJsonWrap={false}
            label='Duration *'
            childList={durationOptions}
            value={durationValues}
            updateValue={handleDurationChange}
            className='box-size-width z-45'
            isDark={true}
            isClearable={true}
            selection='single'
            size='medium'
            placeholder=''
          />
        )}

        {/* End Date (conditional for Egypt) */}
        {(!isEgyptAndApplicableTarget || formData?.duration) && (
          <DatePickerInput
            value={formData?.endDate}
            updateValue={(val, validControl) =>
              handleFieldChange('endDate', val, 'endDateValid', validControl)
            }
            uiSchema={{
              placeholder: 'Choose date',
              className: 'box-size-width z-45',
              label: 'Active End Date *',
              dependentDate: [startDate, linkedTargetDates.latestEndDate],
              disabled: endDateDisabled,
              disabledHelperText: endDateHelperText,
              isDark: true,
              required: false,
              maxDate: linkedTargetDates.maxEndDate,
              validationRules: [
                {type: 'required', msg: ` is required`},
                {
                  type: DATE_VALIDATION_TYPE.START_DATE,
                  msg: `Active End Date cannot be in the past.`,
                  excludeLabel: true
                },
                {
                  type: DATE_VALIDATION_TYPE.END_DATE,
                  msg: ` Target cannot end before start date, please select correct values`,
                  startDate: startDate,
                  excludeLabel: true
                },
                ...(linkedTargetDates.latestEndDate
                  ? [
                      {
                        type: DATE_VALIDATION_TYPE.END_DATE_GREATER,
                        endDate: linkedTargetDates.latestEndDate,
                        msg: ' Must be on or before the Active End Date of the linked Sales Target',
                        excludeLabel: true
                      }
                    ]
                  : [])
              ]
            }}
          />
        )}
      </div>

      {/* Active and Display toggle - Show for all targets including In-Progress */}
      <div className='avail-second-row'>
        <Toggle
          value={!!formData?.activeDisplay}
          updateValue={(val) => {
            const updates = {activeDisplay: val};
            // When toggle is turned ON for In-Progress target, sync Display End Date
            if (val && isInProgressTarget && formData?.endDate) {
              updates.displayEndDate = formData.endDate;
              updates.displayEndDateValid = formData.endDateValid;
            }
            handleFieldChange('activeDisplay', val);
          }}
          uiSchema={{
            className: 'mt-4',
            label: 'Active and Display the same?',
            isDark: true,
            showCheckedIcon: false
          }}
        />
      </div>

      {/* Display Start and End Date - Show when toggle is NO */}
      {showDisplayDates && (
        <div className='flex flex-row flex-wrap gap-6'>
          <DatePickerInput
            value={formData?.displayStartDate}
            updateValue={(val, validControl) =>
              handleFieldChange(
                'displayStartDate',
                val,
                'displayStartDateValid',
                validControl
              )
            }
            uiSchema={{
              className: 'box-size-width z-35',
              placeholder: 'Choose date',
              label: 'Display Start Date *',
              dependentDate: [
                formData?.startDate,
                formData?.displayEndDate,
                formData?.endDate,
                linkedTargetDates.earliestStartDate
              ],
              isDark: true,
              required: false,
              validationRules: isInProgressTarget
                ? [
                    {type: 'required', msg: ` is required`},
                    {
                      type: DATE_VALIDATION_TYPE.END_DATE,
                      msg: ` Display Start Date must be after Active Start Date`,
                      startDate: formData?.startDate,
                      excludeLabel: true
                    }
                  ]
                : [
                    {type: 'required', msg: ` is required`},
                    {
                      type: DATE_VALIDATION_TYPE.START_DATE,
                      msg: ` Display date cannot be in the past`,
                      endDate: formData?.endDate,
                      excludeLabel: true
                    }
                  ]
            }}
          />
          <DatePickerInput
            value={formData?.displayEndDate}
            updateValue={(val, validControl) =>
              handleFieldChange(
                'displayEndDate',
                val,
                'displayEndDateValid',
                validControl
              )
            }
            uiSchema={{
              placeholder: 'Choose date',
              className: 'box-size-width z-25',
              label: 'Display End Date *',
              dependentDate: [
                displayStartDate,
                formData?.endDate,
                linkedTargetDates.latestEndDate
              ],
              disabled: !displayStartDate,
              disabledHelperText:
                ' Please select start date before selecting end date.',
              isDark: true,
              required: false,
              validationRules: [
                {type: 'required', msg: ` is required`},
                {
                  type: DATE_VALIDATION_TYPE.END_DATE,
                  msg: ` Display end date can be on or after the active end date`,
                  startDate: formData?.endDate,
                  excludeLabel: true
                }
              ]
            }}
          />
        </div>
      )}
    </div>
  );
};

AvailabilitySection.propTypes = {
  formData: PropTypes.object.isRequired,
  countryCode: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  salesTarget: PropTypes.object,
  disableStartDate: PropTypes.bool,
  disableEndDate: PropTypes.bool
};

export default React.memo(AvailabilitySection);
